import React from 'react';
import { HiOutlineChatBubbleLeftRight } from 'react-icons/hi2';
import { useDiscussThread } from '../../hooks/useDiscussThread';

/**
 * "Discuss" affordance shared by expenses, activities and swaps.
 *
 * Deliberately quiet by default (`variant="icon"`): these buttons sit on rows
 * people scan dozens at a time, and a loud CTA on every one would compete with
 * the actions that settle or edit the record. The labeled variant is for
 * detail views where there is room for it.
 *
 * @param {Object} props
 * @param {'expense'|'activity'|'swap'} props.type
 * @param {string} props.id - the linked record's id
 * @param {string} props.label - human label shown in the thread header
 * @param {string[]} [props.childIds] - seeds the conversation's child tags
 * @param {'icon'|'labeled'} [props.variant]
 */
const DiscussButton = ({ type, id, label, childIds = [], variant = 'icon', className = '' }) => {
  const { openDiscussion, opening } = useDiscussThread();

  const handleClick = (e) => {
    // These buttons live inside clickable rows and expandable cards.
    e.stopPropagation();
    e.preventDefault();
    openDiscussion({ type, id, label, childIds });
  };

  if (variant === 'labeled') {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={opening}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold text-accent-700 bg-accent-50 hover:bg-accent-100 transition-colors disabled:opacity-40 ${className}`}
        title="Discuss with your co-parent"
      >
        <HiOutlineChatBubbleLeftRight className="text-sm" />
        Discuss
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={opening}
      className={`w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:text-accent-600 hover:bg-surface-100 transition-colors disabled:opacity-40 ${className}`}
      title="Discuss with your co-parent"
      aria-label="Discuss with your co-parent"
    >
      <HiOutlineChatBubbleLeftRight className="text-sm" />
    </button>
  );
};

export default DiscussButton;
